import { useEffect, useState } from 'react'
import { loc } from '../data/cards'
import { useMainButton } from '../components/MainButton'
import { GENDERS, GOALS, PATHS, SIGNS, signById, toGender } from '../data/signs'
import type { T, TKey } from '../i18n'
import { addressName } from '../../../shared/name.ts'
import { LANGS, type Lang } from '../data/cards'
import { LANG_NAMES } from '../i18n'
import { haptic } from '../lib/telegram'
import { useSirr } from '../store'
import type { Profile } from '../lib/storage'

interface Props {
  step: number
  onNext: () => void
  onFinish: () => void
  /** Возврат к уже отвеченному вопросу — по нажатию на свою же реплику. */
  onGoTo: (step: number) => void
}

/**
 * Знакомство идёт разговором, а не анкетой.
 *
 * Вопрос задаёт персонаж, ответ ложится пузырём справа и остаётся
 * редактируемым. Под каждым вопросом — строка «зачем спрашиваем»: вопрос без
 * причины читается как сбор данных, а с причиной — как забота о точности
 * толкования.
 *
 * Шаг живёт в `App.tsx`: там же полоса прогресса и кнопка «назад», и разводить
 * их с лентой нельзя — «назад» обязан возвращать к предыдущему вопросу, а не
 * закрывать онбординг целиком.
 */
interface Question {
  step: number
  ask: TKey
  why: TKey
  /**
   * Отклик на ответ — его говорят, прежде чем задать следующий вопрос.
   * У последнего вопроса его нет: за него не выходят, и такой отклик никто
   * никогда не увидел бы. Конец разговора закрывает `obDone`.
   */
  ack?: TKey
  /** Заголовок раздела перед вопросом. Длинная анкета читается как несколько коротких. */
  part?: TKey
}

const QUESTIONS: Question[] = [
  // Имя спрашиваем первым — как в нативе. Раньше веб его не спрашивал вовсе и
  // брал из Telegram или Google, а там сплошь «Mike S.», один инициал или почта.
  // Имя уходит в обращение модели и в уведомление, и такое там не годится.
  // Язык — первым и до всего: на нём же задаются следующие вопросы. Берём не из
  // настроек телефона, потому что в Азербайджане азербайджанского там у многих
  // нет — телефон стоит на русском или турецком, а приложение на родном языке
  // им как раз и нужно.
  { step: 1, ask: 'askLang', why: 'whyLang', ack: 'ackLang' },
  { step: 2, ask: 'nameTitle', why: 'nameSub', ack: 'ackName' },
  { step: 3, ask: 'genderTitle', why: 'genderSub', ack: 'ackGender' },
  { step: 4, ask: 'goalsTitle', why: 'whyGoals', ack: 'ackGoals' },
  { step: 5, ask: 'askPath', why: 'whyPath', ack: 'ackPath' },
  { step: 6, ask: 'askSign', why: 'signSub', part: 'astroTitle' },
]

export const OB_LAST = QUESTIONS[QUESTIONS.length - 1].step

/**
 * Реплики и наборы выбора — снаружи `Onboarding`, а не вложенными функциями.
 *
 * Вложенный компонент React считает новым типом на каждом рендере родителя:
 * поддерево размонтируется и соберётся заново. Здесь это пока не бросалось в
 * глаза — своего состояния и анимаций внутри нет, а поле имени лежит отдельно.
 * Но набор имени перерисовывал весь разговор на каждую букву, и любая мелочь
 * внутри (фокус, анимация появления) сразу превратилась бы в ту же поломку,
 * что случилась с вопросом по раскладу: клавиатура, слетающая после буквы.
 */
interface PickProps {
  profile: Profile
  update: (patch: Partial<Profile> | ((p: Profile) => Partial<Profile>)) => void
  lang: Lang
}

/** Заданный вопрос: реплика персонажа и под ней строка «зачем спрашиваем». */
function Ask({ q, t }: { q: Question; t: T }) {
  return (
    <>
      {q.part && <div className="obpart">{t(q.part)}</div>}
      <div className="bubble bubble--p">
        {t(q.ask)}
        <span className="obwhy">{t(q.why)}</span>
      </div>
    </>
  )
}

/** Уже пройденный вопрос: реплика, ответ человека и отклик на него. */
function Say({
  q,
  answer,
  ack,
  onEdit,
  t,
}: {
  q: Question
  answer: string
  ack: boolean
  onEdit: () => void
  t: T
}) {
  return (
    <>
      <Ask q={q} t={t} />
      <button type="button" className="bubble bubble--u obedit" onClick={onEdit}>
        {answer}
      </button>
      {ack && q.ack && <div className="bubble bubble--p">{t(q.ack)}</div>}
    </>
  )
}

function GenderPick({ profile, update, lang }: PickProps) {
  return (
    <div className="stack">
      {GENDERS.map((g) => (
        <button
          key={g.id}
          type="button"
          className="pick"
          aria-pressed={profile.gender === g.id}
          style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}
          onClick={() => {
            haptic.select()
            update({ gender: toGender(g.id) })
          }}
        >
          <Sym>{g.sym}</Sym>
          <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 18 }}>
            {loc(g, lang)}
          </div>
        </button>
      ))}
    </div>
  )
}

function GoalsPick({ profile, update, lang }: PickProps) {
  return (
    <div className="grid-2">
      {GOALS.map((g) => (
        <button
          key={g.id}
          type="button"
          className="pick"
          aria-pressed={profile.goals.includes(g.id)}
          style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}
          onClick={() => {
            haptic.select()
            update((p) => ({
              goals: p.goals.includes(g.id)
                ? p.goals.filter((x) => x !== g.id)
                : [...p.goals, g.id],
            }))
          }}
        >
          <span style={{ fontSize: 24, color: 'var(--gold)' }}>{g.sym}</span>
          <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 16 }}>
            {loc(g, lang)}
          </span>
          <span style={{ fontSize: 11, color: 'var(--ink-muted)', lineHeight: 1.3 }}>
            {loc({ ru: g.ruD, az: g.azD, en: g.enD, tr: g.trD }, lang)}
          </span>
        </button>
      ))}
    </div>
  )
}

function PathPick({ profile, update, lang }: PickProps) {
  return (
    <div className="stack">
      {PATHS.map((p) => (
        <button
          key={p.id}
          type="button"
          className="pick"
          aria-pressed={profile.path === p.id}
          style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}
          onClick={() => {
            haptic.select()
            update({ path: p.id })
          }}
        >
          <Sym>{p.sym}</Sym>
          <div>
            <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 18 }}>
              {loc(p, lang)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 2 }}>
              {loc({ ru: p.ruD, az: p.azD, en: p.enD, tr: p.trD }, lang)}
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}

function SignPick({ profile, update, lang }: PickProps) {
  return (
    <div className="grid-4">
      {SIGNS.map((s) => (
        <button
          key={s.id}
          type="button"
          className="pick"
          aria-pressed={profile.zodiac === s.id}
          style={{
            aspectRatio: '1',
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
          onClick={() => {
            haptic.select()
            update({ zodiac: s.id })
          }}
        >
          <span style={{ fontSize: 20, color: 'var(--gold)' }}>{s.glyph}</span>
          <span style={{ fontSize: 9, color: '#c9bcd8' }}>{loc(s, lang)}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * Такт разговора.
 *
 * `0` — персонаж набирает отклик на прошлый ответ, `1` — отклик сказан и он
 * набирает следующий вопрос, `2` — вопрос задан и виден выбор. Два такта, а не
 * один: без отклика человек отвечает в пустоту, и разговор снова становится
 * анкетой, у которой просто скруглили пузыри.
 */
const BEAT_ACK = 480
const BEAT_ASK = 620

export function Onboarding({ step, onNext, onFinish, onGoTo }: Props) {
  const { profile, lang, t, update, setLang } = useSirr()

  const [beat, setBeat] = useState(2)
  useEffect(() => {
    if (step === 0) return
    // На первом вопросе откликаться не на что — только пауза перед вопросом.
    if (step === 1) {
      setBeat(1)
      const id = setTimeout(() => setBeat(2), BEAT_ASK)
      return () => clearTimeout(id)
    }
    setBeat(0)
    const toAck = setTimeout(() => setBeat(1), BEAT_ACK)
    const toAsk = setTimeout(() => setBeat(2), BEAT_ACK + BEAT_ASK)
    return () => {
      clearTimeout(toAck)
      clearTimeout(toAsk)
    }
  }, [step])

  // Лента растёт вниз. Двигаем её напрямую, а не через `scrollIntoView`: тот
  // прокручивает всех прокручиваемых предков разом, включая каркас `.app`,
  // и наверх уезжает всё приложение вместе с шапкой.
  useEffect(() => {
    const body = document.querySelector('.app__body')
    if (body) body.scrollTop = body.scrollHeight
  }, [step, beat])

  const label = step === 0 ? t('start') : step === OB_LAST ? t('obEnter') : t('next')
  // Цели требуют хотя бы одного выбора — у остальных вопросов есть значение
  // по умолчанию. У пола это «не указывать»: заставлять отвечать нельзя,
  // а промпт с таким ответом работает так же, как работал до его появления.
  // Имя вводится текстом, поэтому у него своё состояние: в профиль оно уходит
  // на выходе с шага, а не на каждую букву — иначе синхронизация ездит на сервер
  // за каждое нажатие клавиши.
  const [name, setName] = useState(profile.name ?? '')
  const cleanName = addressName(name)

  // У языка блокировки нет: он уже выбран — либо человеком, либо по устройству.
  const blocked = (step === 2 && cleanName === null) || (step === 4 && profile.goals.length === 0)

  useMainButton({
    label,
    disabled: blocked,
    onClick: () => {
      if (step === 2 && cleanName) update({ name: cleanName })
      if (step === OB_LAST) onFinish()
      else onNext()
    },
  })

  /** Ответ человека одной строкой — тем же текстом, каким он выбирал. */
  const answerOf = (n: number): string => {
    if (n === 1) return LANG_NAMES[lang]
    if (n === 2) return cleanName ?? name
    if (n === 3) return loc(GENDERS.find((g) => g.id === profile.gender) ?? GENDERS[2], lang)
    if (n === 4)
      return GOALS.filter((g) => profile.goals.includes(g.id))
        .map((g) => loc(g, lang))
        .join(', ')
    if (n === 5) return loc(PATHS.find((p) => p.id === profile.path) ?? PATHS[0], lang)
    return loc(signById(profile.zodiac), lang)
  }

  if (step === 0) return <Welcome t={t} />

  return (
    <div className="screen">
      <div className="talk__list">
        <div className="bubble bubble--p">{t('obHello')}</div>

        {QUESTIONS.filter((q) => q.step < step).map((q) => (
          <Say
            key={q.step}
            q={q}
            t={t}
            answer={answerOf(q.step)}
            // Отклик на последний ответ появляется с задержкой — его как раз
            // «набирают»; на более ранние он давно сказан.
            ack={q.step < step - 1 || beat >= 1}
            onEdit={() => {
              haptic.select()
              onGoTo(q.step)
            }}
          />
        ))}

        {beat < 2 ? (
          <div className="bubble bubble--p bubble--typing">
            <span />
            <span />
            <span />
          </div>
        ) : (
          <>
            <Ask q={QUESTIONS[step - 1]} t={t} />
            <div className="obpick">
              {step === 1 && (
                // Названия языков — на самих себе: их узнают, не понимая текущего.
                <div className="obpick__list">
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      className={`pick${lang === l ? ' pick--on' : ''}`}
                      onClick={() => {
                        haptic.select()
                        setLang(l)
                      }}
                    >
                      {LANG_NAMES[l]}
                    </button>
                  ))}
                </div>
              )}
              {step === 2 && (
                <>
                  {/* Имя автоисправлению не отдаём: телефон правит незнакомое
                      слово на словарное, а имя уходит в обращение модели и в
                      заголовок уведомления. */}
                  <input
                    className="askfield"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('nameHint')}
                    autoComplete="given-name"
                    autoCorrect="off"
                    autoCapitalize="words"
                    spellCheck={false}
                    maxLength={40}
                  />
                  {/* Правило показываем всегда, а не только при ошибке: человек
                      должен понимать рамку заранее. */}
                  <div className={`obrule${name !== '' && cleanName === null ? ' obrule--bad' : ''}`}>
                    {t('nameRule')}
                  </div>
                  {cleanName !== null && cleanName !== name.trim() && (
                    // Фамилию отбрасываем — показываем, как обратимся, чтобы это
                    // не выглядело потерей введённого.
                    <div className="obrule">{cleanName}</div>
                  )}
                </>
              )}
              {step === 3 && <GenderPick profile={profile} update={update} lang={lang} />}
              {step === 4 && <GoalsPick profile={profile} update={update} lang={lang} />}
              {step === 5 && <PathPick profile={profile} update={update} lang={lang} />}
              {step === 6 && <SignPick profile={profile} update={update} lang={lang} />}
            </div>
          </>
        )}

        {/* Немедленная отдача: персонаж называет то, что уже узнал, — до всякой
            оплаты и до первого расклада. */}
        {step === OB_LAST && beat === 2 && (
          <div className="bubble bubble--p">
            {t('birthSign', { s: loc(signById(profile.zodiac), lang) })}. {t('obDone')}
          </div>
        )}
      </div>
    </div>
  )






}

/** Круглый значок в строке выбора — общий вид у пола и пути. */
function Sym({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: '50%',
        border: '1px solid rgba(200,162,78,.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        color: 'var(--gold)',
      }}
    >
      {children}
    </div>
  )
}

function Welcome({ t }: { t: (k: TKey) => string }) {
  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 34px',
        gap: 22,
      }}
    >
      <div style={{ animation: 'floaty 5s ease-in-out infinite' }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            border: '1px solid rgba(200,162,78,.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--gold-glow)',
            background:
              'radial-gradient(circle at 50% 40%, rgba(58,30,92,.7), rgba(18,10,31,.9))',
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              border: '1px solid rgba(200,162,78,.7)',
              transform: 'rotate(45deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                transform: 'rotate(-45deg)',
                fontSize: 26,
                color: 'var(--gold)',
                textShadow: '0 0 16px rgba(214,178,94,.7)',
              }}
            >
              ✦
            </span>
          </div>
        </div>
      </div>

      <div style={{ animation: 'fu .8s .1s both' }}>
        <div
          style={{
            fontFamily: 'var(--serif)',
            fontWeight: 700,
            fontStyle: 'italic',
            fontSize: 44,
            letterSpacing: 4,
            lineHeight: 1,
          }}
        >
          SIRR
        </div>
        <div className="eyebrow" style={{ letterSpacing: 5, marginTop: 4 }}>
          {t('tagline')}
        </div>
      </div>

      <p
        className="prose"
        style={{ maxWidth: 270, fontSize: 18, animation: 'fu .8s .25s both', color: '#c9bcd8' }}
      >
        {t('welcomeText')}
      </p>
    </div>
  )
}
